import { useState, useEffect } from 'react'
import { IconCalendar, IconBed, IconToolsKitchen2, IconFileText, IconClipboardList, IconBuildingSkyscraper } from '@tabler/icons-react'

const API = import.meta.env.VITE_API_URL || ''

export default function GuestPortalPage() {
  const [data, setData] = useState(null)
  const [selectedBeo, setSelectedBeo] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const token = window.location.pathname.split('/portal/')[1]

  useEffect(() => {
    if (!token) return setError('Invalid portal link')
    fetch(`${API}/api/guest-portal/view/${token}`).then(r => r.ok ? r.json() : Promise.reject('not found'))
      .then(setData).catch(() => setError('This portal link is no longer active.')).finally(() => setLoading(false))
  }, [token])

  const loadBeo = async (id) => {
    const r = await fetch(`${API}/api/guest-portal/view/${token}/beo/${id}`)
    if (r.ok) setSelectedBeo(await r.json())
  }

  const fmt = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '--'

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="spinner-border text-primary"></div></div>
  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
      <div className="text-center"><IconBuildingSkyscraper size={48} className="text-secondary mb-3" /><h3>Portal Unavailable</h3><p className="text-secondary">{error}</p></div>
    </div>
  )

  const { hotel, event, beos, proposals, contracts, roomPickup } = data

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Header */}
      <div style={{ background: '#1a1a2e', color: '#fff', padding: '24px 0' }}>
        <div className="container" style={{ maxWidth: 900 }}>
          <div className="d-flex align-items-center gap-3">
            <IconBuildingSkyscraper size={36} />
            <div><h2 className="mb-0">{hotel.name}</h2><div style={{ opacity: 0.7, fontSize: '0.9rem' }}>{hotel.city}, {hotel.state}</div></div>
          </div>
        </div>
      </div>

      <div className="container py-4" style={{ maxWidth: 900 }}>
        {/* Event Summary */}
        <div className="card mb-4">
          <div className="card-body">
            <h3 className="mb-3">{event.name}</h3>
            <div className="row g-3">
              <div className="col-md-3 d-flex align-items-center gap-2"><IconCalendar size={18} className="text-blue" /><div><div className="text-secondary" style={{ fontSize: '0.75rem' }}>Dates</div><div className="fw-medium">{fmtDate(event.arrival)} - {fmtDate(event.departure)}</div></div></div>
              <div className="col-md-3 d-flex align-items-center gap-2"><IconBed size={18} className="text-blue" /><div><div className="text-secondary" style={{ fontSize: '0.75rem' }}>Room Block</div><div className="fw-medium">{event.rooms} rooms/night</div></div></div>
              <div className="col-md-3"><div className="text-secondary" style={{ fontSize: '0.75rem' }}>Rate</div><div className="fw-medium">{fmt(event.rate)}/night</div></div>
              <div className="col-md-3"><div className="text-secondary" style={{ fontSize: '0.75rem' }}>Status</div><span className={`badge ${event.status === 'definite' ? 'bg-green-lt' : 'bg-yellow-lt'}`}>{event.status}</span></div>
            </div>
          </div>
        </div>

        {/* BEOs */}
        {beos?.length > 0 && (
          <div className="card mb-4">
            <div className="card-header"><h4 className="card-title mb-0 d-flex align-items-center gap-2"><IconClipboardList size={18} /> Banquet Event Orders</h4></div>
            <div className="list-group list-group-flush">
              {beos.map(b => (
                <button key={b.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onClick={() => loadBeo(b.id)}>
                  <div><span className="fw-medium">{b.title}</span><span className="text-secondary ms-2" style={{ fontSize: '0.85rem' }}>{fmtDate(b.event_date)} {b.start_time && `at ${b.start_time}`}</span></div>
                  <div><span className="badge bg-blue-lt me-2">Rev. {b.revision}</span><span className={`badge ${b.status === 'final' ? 'bg-green-lt' : 'bg-secondary-lt'}`}>{b.status}</span></div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Selected BEO Detail */}
        {selectedBeo && (
          <div className="card mb-4 border-blue">
            <div className="card-header bg-blue-lt d-flex justify-content-between">
              <h4 className="card-title mb-0">{selectedBeo.title} (Rev. {selectedBeo.revision})</h4>
              <button className="btn-close" onClick={() => setSelectedBeo(null)}></button>
            </div>
            <div className="card-body">
              <div className="row g-2 mb-3" style={{ fontSize: '0.9rem' }}>
                <div className="col-4"><strong>Date:</strong> {fmtDate(selectedBeo.event_date)}</div>
                <div className="col-4"><strong>Time:</strong> {selectedBeo.start_time || '--'} - {selectedBeo.end_time || '--'}</div>
                <div className="col-4"><strong>Location:</strong> {selectedBeo.location || '--'}</div>
                <div className="col-4"><strong>Setup:</strong> {selectedBeo.setup_style}</div>
                <div className="col-4"><strong>Guests:</strong> {selectedBeo.guest_count}</div>
              </div>
              {['setup', 'food', 'beverage', 'av', 'notes'].map(sec => {
                const items = (selectedBeo.items || []).filter(i => i.section === sec)
                if (!items.length) return null
                return (
                  <div key={sec} className="mb-3">
                    <h5 className="text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '0.05em', borderBottom: '1px solid #dee2e6', paddingBottom: 4 }}>
                      {{ setup: 'Room Setup', food: 'Food', beverage: 'Beverages', av: 'AV & Equipment', notes: 'Notes' }[sec]}
                    </h5>
                    {items.map(item => <div key={item.id} className="d-flex justify-content-between py-1"><span>{item.description}</span>{item.price > 0 && <span className="text-secondary">{item.quantity > 1 ? `${item.quantity} x ` : ''}{fmt(item.price)}</span>}</div>)}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Proposals & Contracts */}
        <div className="row g-4 mb-4">
          {proposals?.length > 0 && (
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-header"><h4 className="card-title mb-0 d-flex align-items-center gap-2"><IconFileText size={18} /> Proposals</h4></div>
                <div className="list-group list-group-flush">
                  {proposals.map(p => (
                    <div key={p.id} className="list-group-item d-flex justify-content-between"><div><div className="fw-medium">{p.title}</div><div className="text-secondary" style={{ fontSize: '0.8rem' }}>{fmtDate(p.sent_date)}</div></div><div className="text-end"><div className="fw-medium">{fmt(p.total)}</div><span className={`badge ${p.status === 'accepted' ? 'bg-green-lt' : 'bg-blue-lt'}`}>{p.status}</span></div></div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {contracts?.length > 0 && (
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-header"><h4 className="card-title mb-0">Contracts</h4></div>
                <div className="list-group list-group-flush">
                  {contracts.map(c => (
                    <div key={c.id} className="list-group-item d-flex justify-content-between"><div className="fw-medium">{c.title}</div><div className="text-end"><div className="fw-medium">{fmt(c.amount)}</div><span className={`badge ${c.status === 'signed' ? 'bg-green-lt' : 'bg-yellow-lt'}`}>{c.status}</span></div></div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Room Pickup */}
        {roomPickup?.length > 0 && (
          <div className="card mb-4">
            <div className="card-header"><h4 className="card-title mb-0">Room Block Pickup</h4></div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table table-striped mb-0">
                <thead><tr><th>Date</th><th className="text-end">Blocked</th><th className="text-end">Picked Up</th><th className="text-end">Remaining</th></tr></thead>
                <tbody>
                  {roomPickup.map(r => (
                    <tr key={r.id}><td>{fmtDate(r.pickup_date)}</td><td className="text-end">{r.blocked}</td><td className="text-end fw-medium">{r.picked_up}</td><td className="text-end">{r.blocked - r.picked_up}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="text-center text-secondary py-3" style={{ fontSize: '0.8rem' }}>Powered by <strong>Leadiox</strong></div>
      </div>
    </div>
  )
}
